import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HostelCard from "@/components/HostelCard";
import HostelCardSkeleton from "@/components/HostelCardSkeleton";
import HostelBrowseMap from "@/components/HostelBrowseMap";
import SearchBar from "@/components/SearchBar";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { SlidersHorizontal, MapPin, X, List, Map as MapIcon } from "lucide-react";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function Browse() {
  const q = useQuery();
  const nav = useNavigate();
  const [hostels, setHostels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxPrice, setMaxPrice] = useState(parseInt(q.get("max_price") || "1000"));
  const [gender, setGender] = useState(q.get("gender") || "any");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState(q.get("view") === "map" ? "map" : "list");

  // Persist the view mode in the URL so it survives a refresh / share.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (viewMode === "map") params.set("view", "map"); else params.delete("view");
    const search = params.toString();
    window.history.replaceState(null, "", search ? `?${search}` : window.location.pathname);
  }, [viewMode]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (q.get("city")) params.set("city", q.get("city"));
    if (q.get("coaching")) params.set("coaching", q.get("coaching"));
    if (q.get("q")) params.set("q", q.get("q"));
    if (gender !== "any") params.set("gender", gender);
    params.set("max_price", maxPrice);
    setLoading(true);
    api
      .get(`/hostels?${params}`)
      .then((r) => setHostels(r.data))
      .catch(() => setHostels([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.get("city"), q.get("coaching"), q.get("q"), gender, maxPrice]);

  const activeFilterCount = useMemo(
    () => (gender !== "any" ? 1 : 0) + (maxPrice !== 1000 ? 1 : 0),
    [gender, maxPrice]
  );

  const clearFilters = () => {
    setGender("any");
    setMaxPrice(1000);
  };

  const FiltersPanel = (
    <div>
      <div className="flex items-center justify-between">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Filters</div>
        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={clearFilters}
            data-testid="filter-clear-btn"
            className="text-xs font-semibold text-orange-800 hover:underline"
          >
            Clear all
          </button>
        )}
      </div>

      <div className="mt-6">
        <Label className="text-xs tracking-widest uppercase font-bold text-slate-700">Max price / night</Label>
        <div className="mt-3">
          <Slider data-testid="filter-price" value={[maxPrice]} onValueChange={(v) => setMaxPrice(v[0])} min={200} max={1000} step={50} />
          <div className="mt-2 text-sm font-semibold text-orange-800">Up to ₹{maxPrice}</div>
        </div>
      </div>

      <div className="mt-8">
        <Label className="text-xs tracking-widest uppercase font-bold text-slate-700">Gender</Label>
        <RadioGroup value={gender} onValueChange={setGender} className="mt-3 space-y-2">
          {[
            ["any", "Any"], ["boys", "Boys only"], ["girls", "Girls only"], ["coed", "Co-ed"],
          ].map(([v, l]) => (
            <div key={v} className="flex items-center gap-2">
              <RadioGroupItem value={v} id={`g-${v}`} data-testid={`filter-gender-${v}`} />
              <Label htmlFor={`g-${v}`} className="text-sm cursor-pointer">{l}</Label>
            </div>
          ))}
        </RadioGroup>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FBF5EA]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
        <SearchBar compact />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 grid lg:grid-cols-12 gap-8">
        {/* Desktop sidebar filters (unchanged position) */}
        <aside className="hidden lg:block lg:col-span-3">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 sticky top-24">
            {FiltersPanel}
          </div>
        </aside>

        <main className="lg:col-span-9">
          {/* Result header — height reserved so it doesn't jump between loading/loaded */}
          <div className="flex items-center justify-between mb-6 gap-3 min-h-[3.5rem]">
            <h1 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tighter" data-testid="browse-result-heading">
              {loading ? (
                <span className="inline-block h-8 sm:h-9 w-56 rounded-md bg-sandstone-100 shimmer align-middle" />
              ) : (
                <>
                  {hostels.length} hostel{hostels.length === 1 ? "" : "s"}
                  {q.get("city") ? ` in ${q.get("city")}` : " found"}
                </>
              )}
            </h1>

            {/* View mode toggle — always visible */}
            <div
              data-testid="browse-view-toggle"
              role="tablist"
              aria-label="View mode"
              className="inline-flex items-center rounded-full border border-slate-300 bg-white p-0.5 shadow-sm"
            >
              <button
                type="button"
                role="tab"
                aria-selected={viewMode === "list"}
                data-testid="browse-view-list"
                onClick={() => setViewMode("list")}
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-semibold transition-colors ${viewMode === "list" ? "bg-orange-800 text-white" : "text-slate-700 hover:bg-slate-50"}`}
              >
                <List size={13} /> List
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={viewMode === "map"}
                data-testid="browse-view-map"
                onClick={() => setViewMode("map")}
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-semibold transition-colors ${viewMode === "map" ? "bg-orange-800 text-white" : "text-slate-700 hover:bg-slate-50"}`}
              >
                <MapIcon size={13} /> Map
              </button>
            </div>

            {/* Mobile filter trigger — visible only <lg */}
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button
                  data-testid="mobile-filters-btn"
                  variant="outline"
                  className="lg:hidden rounded-full border-orange-300 text-orange-800 hover:bg-orange-50 font-semibold gap-2 h-11 px-4"
                >
                  <SlidersHorizontal size={16} /> Filters
                  {activeFilterCount > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-orange-800 text-white text-[10px] font-black">
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle className="font-display text-2xl text-left">Filter hostels</SheetTitle>
                </SheetHeader>
                <div className="mt-4">{FiltersPanel}</div>
                <Button
                  onClick={() => setFiltersOpen(false)}
                  className="w-full mt-6 rounded-full h-12 bg-orange-800 hover:bg-orange-900 text-white font-semibold"
                  data-testid="mobile-filters-apply"
                >
                  Show {hostels.length} hostel{hostels.length === 1 ? "" : "s"}
                </Button>
              </SheetContent>
            </Sheet>
          </div>

          {/* Grid — same slot for loading / results / empty so no layout jumps.
              When the user flips to Map view, we render the map instead. */}
          {viewMode === "map" ? (
            loading ? (
              <div className="rounded-2xl bg-slate-100 animate-pulse" style={{ height: "calc(100vh - 260px)", minHeight: 460 }} />
            ) : (
              <HostelBrowseMap hostels={hostels} />
            )
          ) : loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6" data-testid="browse-loading-grid">
              {Array.from({ length: 6 }).map((_, i) => <HostelCardSkeleton key={i} />)}
            </div>
          ) : hostels.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 sm:p-16 text-center" data-testid="browse-empty-state">
              <MapPin className="mx-auto text-orange-800/60 mb-3" size={32} />
              <div className="text-slate-700 font-semibold">No hostels match your filters.</div>
              <div className="text-slate-500 text-sm mt-1">Try widening your search — raise the price or clear the gender filter.</div>
              {activeFilterCount > 0 && (
                <Button
                  onClick={clearFilters}
                  variant="outline"
                  className="mt-4 rounded-full border-orange-300 text-orange-800 hover:bg-orange-50 font-semibold gap-2"
                  data-testid="browse-empty-clear-btn"
                >
                  <X size={14} /> Clear all filters
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6" data-testid="browse-results-grid">
              {hostels.map((h) => <HostelCard key={h.id} h={h} />)}
            </div>
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
}
